// @flow

import React, { Component } from 'react';
import glamorous from 'glamorous';
import { connect } from 'react-redux';
import pluralize from 'pluralize';
import { compose, withProps } from 'recompose';
import { map, debounce } from 'lodash/fp';
import Rows from 'app/components/layout/Rows';
import { fetchGroups } from 'app/actions/groupActions';
import t from 'app/utils/translate';
import filterGroupsSelector from 'app/selectors/friends/filterGroupsSelector';
import EntrantRow from 'app/components/static/EntrantRow';
import FriendsLoader from 'app/components/friends/common/FriendsLoader';
import { FRIEND, NOT_FRIEND } from 'app/constants/friendStatusTypes';
import LeaderBoardFilterBarContent from 'app/components/challenge/LeaderBoardFilterBarContent';
import Loader from 'app/components/commons/Loader';
import Spinner from 'app/components/commons/Spinner';
import decodeEntrant from 'app/normalizers/decodeEntrant';
import { getChallengeUsers } from 'app/api/challenges';
import withAPICollection from 'app/hoc/withAPICollection';
import { IN_PROGRESS } from '../../constants/challengeStatusTypes';
import SearchBar from '../commons/SearchBar';

type Props = {
  challengeId: string,
  competitors: Array<Object>,
  countries: Array<Object>,
  currentPage: number,
  entryCap: number,
  entryCount: number,
  friendGroupsInitialised: boolean,
  groups: Array<FriendGroup>,
  hasNextPage: boolean,
  isLoading: boolean,
  isSignedIn: boolean,
  isUnlimited: boolean,
  maxEntriesPerPerson: number,
  onFetchEntries: () => void,
  onFetchGroups: () => void,
  scheme?: 'light' | 'dark',
  statusId: number,
};

type State = {
  filteredCountryId: ?number,
  filteredGroupId: ?number | ?string,
  isNextLoading: boolean,
  isPrevLoading: boolean,
  searchTerm?: string,
};

const Wrapper = glamorous.div(({ theme }) => ({
  position: 'relative',
  [theme.mediaQuery.desktopAndUp]: {
    paddingLeft: theme.spacing.large * 2,
    paddingRight: theme.spacing.large * 2,
  },
}));

const NoEntrantsMessage = glamorous.div({
  textAlign: 'center',
});

const Main = glamorous.div({
  position: 'relative',
});

const Foot = glamorous.div({
  display: 'flex',
  justifyContent: 'space-between',
  paddingTop: '1rem',
});

const PageButton = glamorous(props =>
  (props.isLoading ? <Spinner kind="dark" {...props} /> : <button type="button" {...props} />))(({ placement }) =>
  (placement === 'right'
    ? {
      marginLeft: 'auto',
    }
    : undefined));

const CenteredText = glamorous.div({
  textAlign: 'center',
  margin: '0 auto',
});

const SmallText = glamorous.span(({ theme }) => ({
  fontSize: theme.fontSize.xsmall,
  opacity: 0.8,
  paddingLeft: '1em',
}));

const StyledSearchBar = glamorous(SearchBar)(({ theme }) => ({
  borderRadius: 4,
  marginTop: theme.spacing.small,
  [theme.mediaQuery.desktopAndUp]: {
    maxWidth: '50%',
  },
}));

class EntrantsContent extends Component<Props, State> {
  props: Props;

  state: State = {
    filteredCountryId: undefined,
    filteredGroupId: undefined,
    isNextLoading: false,
    isPrevLoading: false,
    searchTerm: undefined,
  };

  static defaultProps = {
    scheme: 'light',
  };

  componentDidMount() {
    const { isSignedIn } = this.props;

    if (isSignedIn) {
      this.props.onFetchGroups();
    }

    this.props.onFetchEntries();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { isLoading } = this.props;

    if (isLoading && !nextProps.isLoading) {
      this.setState({
        isNextLoading: false,
        isPrevLoading: false,
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { isSignedIn } = this.props;

    if (isSignedIn && !prevProps.isSignedIn) {
      this.props.onFetchGroups();
    }
  }

  searchEntries = debounce(1000, () => {
    this.props.onFetchEntries({
      user_name: this.state.searchTerm,
    });
  });

  handleSearchChange = (searchTerm: string) => {
    this.setState(
      () => ({
        searchTerm,
        filteredCountryId: undefined,
        filteredGroupId: undefined,
      }),
      () => {
        this.searchEntries();
      },
    );
  };

  handleClearSearch = () => {
    this.setState(() => ({
      searchTerm: undefined,
      filteredCountryId: undefined,
      filteredGroupId: undefined,
    }));
    this.props.onFetchEntries();
  };

  handleCancelSearch = () => {
    this.handleClearSearch();
  };

  handleCountryChange = (countryId: number) => {
    this.setState(
      state => ({
        filteredCountryId: countryId !== state.filteredCountryId ? countryId : undefined,
        filteredGroupId: undefined,
      }),
      () => {
        this.props.onFetchEntries({
          countries: this.state.filteredCountryId,
        });
      },
    );
  };

  handleGroupChange = (groupId: number) => {
    this.setState(
      state => ({
        filteredCountryId: undefined,
        filteredGroupId: groupId !== state.filteredGroupId ? groupId : undefined,
      }),
      () => {
        this.props.onFetchEntries({
          groups: this.state.filteredGroupId,
        });
      },
    );
  };

  handlePrev = () => {
    const { currentPage } = this.props;

    this.setState(
      {
        isPrevLoading: true,
      },
      () => {
        this.props.onFetchEntries({
          countries: this.state.filteredCountryId,
          groups: this.state.filteredGroupId,
          page: currentPage - 1,
        });
      },
    );
  };

  handleNext = () => {
    const { currentPage } = this.props;

    this.setState(
      {
        isNextLoading: true,
      },
      () => {
        this.props.onFetchEntries({
          countries: this.state.filteredCountryId,
          groups: this.state.filteredGroupId,
          page: currentPage + 1,
        });
      },
    );
  };

  render() {
    const {
      challengeId,
      competitors,
      countries,
      currentPage,
      entryCap,
      entryCount,
      friendGroupsInitialised,
      groups,
      hasNextPage,
      isLoading,
      isSignedIn,
      isUnlimited,
      maxEntriesPerPerson,
      scheme,
      statusId,
    } = this.props;
    const {
      searchTerm,
      filteredGroupId,
      filteredCountryId,
      isNextLoading,
      isPrevLoading,
    } = this.state;

    return (
      <Wrapper>
        <CenteredText>
          {entryCount} / {entryCap}{isUnlimited && statusId < IN_PROGRESS ? '+' : ''}
          <SmallText>
            Max {maxEntriesPerPerson} {pluralize('entries', maxEntriesPerPerson)} per person
          </SmallText>
        </CenteredText>
        <StyledSearchBar
          value={searchTerm}
          onSearch={this.handleSearchChange}
          placeholder="Search for an entrant"
          autoFocus={false}
          onClear={this.handleClearSearch}
          onCancel={this.handleCancelSearch}
          scheme={scheme}
        />
        {!searchTerm && (
          <LeaderBoardFilterBarContent
            challengeId={challengeId}
            countries={countries}
            filteredCountryId={filteredCountryId}
            filteredGroupId={filteredGroupId}
            friendGroups={groups}
            isLoading={isSignedIn && !friendGroupsInitialised}
            onCountryChange={this.handleCountryChange}
            onGroupChange={this.handleGroupChange}
            scheme={scheme}
          />
        )}
        {competitors.length === 0 && !isLoading && (
          <NoEntrantsMessage>
            {filteredGroupId
              ? t('There are no entries that meet the filter.')
              : t('There are no entries.')}
          </NoEntrantsMessage>
        )}
        <Main>
          {isLoading && (competitors.length === 0 ? (
            <Loader kind="dark" />
          ) : (
            <FriendsLoader showOverlay inline>
              {t('Loading entries')}...
            </FriendsLoader>
          ))}
          <Rows mobile={2} desktop={4}>
            {competitors.map(entrant => (
              <EntrantRow
                key={entrant.id}
                avatar={entrant.avatar}
                countryFlag={entrant.countryImage}
                entryCount={entrant.entryCount}
                friendStatus={entrant.isFriend ? FRIEND : NOT_FRIEND}
                isVIP={entrant.isVIP}
                name={entrant.userName}
              />
            ))}
          </Rows>
        </Main>
        <Foot>
          {currentPage > 1 && (
            <PageButton placement="left" isLoading={isPrevLoading} onClick={this.handlePrev}>
              Prev
            </PageButton>
          )}
          {hasNextPage && (
            <PageButton placement="right" isLoading={isNextLoading} onClick={this.handleNext}>
              Next
            </PageButton>
          )}
        </Foot>
      </Wrapper>
    );
  }
}

const apiWrapper = compose(
  withAPICollection({
    fetcher: getChallengeUsers,
    normalizeData: map(decodeEntrant),
  }),
  withProps(props => ({
    competitors: props.items,
    currentPage: props.currentPage,
    isLoading: props.loading,
    hasNextPage: props.hasNextPage,
    onFetchEntries(query) {
      props.onFetch(props.challengeId, query);
    },
  })),
);

const connector = connect(
  state => ({
    friendGroupsInitialised: state.friendGroups.hasInitialised,
    groups: filterGroupsSelector(state),
    isSignedIn: state.user.isSignedIn,
  }),
  {
    onFetchGroups: fetchGroups,
  },
);

export default compose(apiWrapper, connector)(EntrantsContent);
